/**
 * The grant editor's tree — markets three levels deep, indicators two.
 *
 * Pure, and here rather than in `AdminPanel.tsx`, for the reason
 * `lib/layout-templates.ts` gives: the interesting logic must not live in the
 * one place this app cannot test.
 *
 * ## A wildcard is not a parent whose children are all ticked
 *
 * The distinction this whole module is shaped around. `group:US:*` and `ind:*`
 * cover **what is added later**; a bucket is an enumeration of today's members,
 * expanded at the moment it was clicked. `matches()` in
 * `entitlements/caps.py` answers *yes* to any value in a wildcard's domain, and
 * to nothing a bucket did not list.
 *
 * So a wildcard is its own row, worded as a promise about the future, and never
 * folded into the parent above the values it happens to cover. Presented the
 * other way round they look identical on screen and behave differently forever
 * after — a category added next month is included by one and locked by the
 * other.
 *
 * ## Standard is a list, not "everything else"
 *
 * `bucketsFor` files an unknown category under **Uncategorised** rather than
 * into Standard. "Everything that is not Seasonality or PD" would ship the next
 * premium category to the free tier the day it merges, silently and with a tick
 * already in the box. A visible bucket nobody granted is the safe failure.
 */

/** One row of the tree. A node with no children is a leaf. */
export interface GrantNode {
  /** Stable across renders — the expand state is keyed on it. */
  id: string;
  label: string;
  hint?: string;
  /** What ticking this row alone writes. Absent on a grouping row. */
  capability?: string;
  /** Whether `capability` covers values added later. Worded differently. */
  wildcard?: boolean;
  children: GrantNode[];
}

export type TickState = "on" | "off" | "mixed";

/** Every capability a tick on this node would write, wildcards excluded. */
export function capabilitiesUnder(node: GrantNode): string[] {
  if (node.wildcard) return [];
  const own = node.capability ? [node.capability] : [];
  return [...own, ...node.children.flatMap(capabilitiesUnder)];
}

/**
 * Whether this row reads ticked, clear, or part-way.
 *
 * A wildcard row answers only for itself: it is held or it is not, and the
 * values it happens to cover say nothing about it.
 */
export function stateOf(node: GrantNode, holds: (capability: string) => boolean): TickState {
  if (node.wildcard && node.capability) return holds(node.capability) ? "on" : "off";
  const caps = capabilitiesUnder(node);
  if (caps.length === 0) return "off";
  const on = caps.filter(holds).length;
  if (on === 0) return "off";
  return on === caps.length ? "on" : "mixed";
}

// ── indicators ───────────────────────────────────────────────────────────────

/**
 * The categories that travel together as the built-in set.
 *
 * Written out rather than derived, so a category added to the catalog lands in
 * `Uncategorised` and has to be filed by hand. See the module docstring.
 */
export const STANDARD_CATEGORIES: readonly string[] = [
  "Overlap Studies",
  "Volatility Indicators",
  "Volume Indicators",
  "Momentum Indicators",
  "Statistic Functions",
  "Series Operators",
  "Price Transform",
  "Symbol Indicators",
  "Universe Rank",
  "Fundamentals",
  "Bar Intelligence",
];

/** Categories that are their own bucket, because they are sold on their own. */
export const OWN_BUCKET_CATEGORIES: readonly string[] = ["Seasonality", "PD", "JP", "JAS", "Breadth", "Patterns"];

/**
 * Indicator categories as buckets, in the order the picker uses.
 *
 * A single-category bucket carries the category's own capability, so ticking
 * *Seasonality* writes one row rather than opening a branch with one child in
 * it.
 */
export function bucketsFor(categories: readonly string[]): GrantNode[] {
  const known = new Set(categories);
  const nodes: GrantNode[] = [];

  const standard = STANDARD_CATEGORIES.filter((category) => known.has(category));
  if (standard.length > 0) {
    nodes.push({
      id: "ind:bucket:standard",
      label: "Standard",
      hint: `${standard.length} categories`,
      children: standard.map((category) => ({
        id: `ind:${category}`,
        label: category,
        capability: `ind:${category}`,
        children: [],
      })),
    });
  }

  for (const category of OWN_BUCKET_CATEGORIES) {
    if (!known.has(category)) continue;
    nodes.push({
      id: `ind:${category}`,
      label: category,
      capability: `ind:${category}`,
      children: [],
    });
  }

  // Anything the map has never heard of, named so it cannot be granted by
  // accident and cannot be missed either.
  const filed = new Set([...STANDARD_CATEGORIES, ...OWN_BUCKET_CATEGORIES]);
  const stray = categories.filter((category) => !filed.has(category));
  if (stray.length > 0) {
    nodes.push({
      id: "ind:bucket:uncategorised",
      label: "Uncategorised",
      hint: "added since this list was written",
      children: stray.map((category) => ({
        id: `ind:${category}`,
        label: category,
        capability: `ind:${category}`,
        children: [],
      })),
    });
  }

  return nodes;
}

// ── markets and universes ────────────────────────────────────────────────────

/** How a symbol group's `category` reads in the tree. Unknown keeps its own name. */
const CATEGORY_LABELS: Record<string, string> = {
  broad: "Broad market",
  cap: "Cap bands",
  exchange: "Exchanges",
  sector: "Sectors",
  thematic: "Themes",
  strategy: "Strategies",
  index: "Indices",
};

/**
 * `"United States — every universe"` -> `"United States"`.
 *
 * The vocabulary writes the scope's own label for a flat list, where the row
 * *is* the wildcard. Here the wildcard is a child and says so itself, so the
 * market keeps only its name and the promise is made once.
 */
function marketLabel(label: string): string {
  const [name] = label.split(" — ");
  return (name ?? label).trim();
}

/** What the vocabulary offers under the `group` domain. */
export interface GroupValue {
  value: string;
  label: string;
  market?: string | null;
}

/** What `fetchGroups()` returns, narrowed to what the tree needs. */
export interface GroupFacts {
  code: string;
  market: string | null;
  category: string | null;
  groupType?: string;
  memberCount?: number;
}

/**
 * Markets, their categories, and the universes inside them.
 *
 * `values` carries two kinds and they are told apart by the `:*` suffix the
 * server puts on a market scope — that is the vocabulary's own spelling
 * (`routers/admin.py` builds `{code}:*`), not a convention invented here.
 *
 * **`facts` is what adds the middle level**, and its absence is survivable: with
 * no catalog the universes sit directly under their market. A tree one level
 * shallower is a worse picker; a tree that failed to render is a broken screen.
 *
 * **The `trades` family is dropped.** `list_groups(None, None, None)` returns
 * the journal's own five buckets — Plan, Danger Zone, In Profit, Completed,
 * Folio — which are seeded per user, gated by `feat:journal` and resolved from
 * the caller's own journal. They are not universes anybody sells, and offering
 * them here invites a grant that would mean nothing.
 */
export function marketTree(
  values: readonly GroupValue[],
  facts: readonly GroupFacts[] = [],
): GrantNode[] {
  const byCode = new Map(facts.map((fact) => [fact.code, fact]));
  const journal = new Set(
    facts.filter((fact) => fact.groupType === "trades").map((fact) => fact.code),
  );

  const scopes = values.filter((value) => value.value.endsWith(":*"));
  const universes = values.filter(
    (value) => !value.value.endsWith(":*") && !journal.has(value.value),
  );

  const nodes: GrantNode[] = [];
  for (const scope of scopes) {
    const market = scope.value.slice(0, -2);
    const mine = universes.filter(
      (value) => (byCode.get(value.value)?.market ?? value.market ?? "") === market,
    );

    const byCategory = new Map<string, GroupValue[]>();
    for (const value of mine) {
      const category = byCode.get(value.value)?.category ?? "";
      const bucket = byCategory.get(category);
      if (bucket) bucket.push(value);
      else byCategory.set(category, [value]);
    }

    const children: GrantNode[] = [];
    for (const [category, members] of byCategory) {
      const leaves = members.map((value) => ({
        id: `group:${value.value}`,
        label: value.label,
        hint: byCode.get(value.value)?.memberCount?.toString(),
        capability: `group:${value.value}`,
        children: [],
      }));
      // No catalog, or a group the catalog does not carry: the universes hang
      // straight off their market rather than under an empty heading.
      if (!category) children.push(...leaves);
      else
        children.push({
          id: `group:${market}:${category}`,
          label: CATEGORY_LABELS[category] ?? category,
          hint: `${leaves.length}`,
          children: leaves,
        });
    }

    // **The wildcard is a child, not the market row itself.** Hanging
    // `group:US:*` on the row that also has the categories under it would make
    // one control mean two things — held, or something below is held — which is
    // exactly the conflation this module exists to avoid. It costs a row per
    // market and it is the row that says what the others cannot.
    nodes.push({
      id: `group:${market}`,
      label: marketLabel(scope.label),
      children: [
        {
          id: `group:${market}:*`,
          label: "Every universe, and any added later",
          capability: `group:${scope.value}`,
          wildcard: true,
          children: [],
        },
        ...children,
      ],
    });
  }
  return nodes;
}
